import buildClientAPI from 'views/providers/clientAPI'
import {
  MANUALLY_ACTIVATED,
  DEFAULT_VARIANT_NAME,
  ExperimentName,
  TrackingEvent,
  FIRST_IMPRESSION
} from 'shared/experiments/consts'
import store from 'shared/store'

import { Experiment } from 'server/shop-config/types'
import {
  ECONDA_SESSION_ID_COOKIE,
  ECONDA_VISITOR_ID_COOKIE
} from 'shared/consts/econdaCookieIds'

import { getCookieByName } from 'shared/utils/getCookie'

// TODO Split DOM and data structure utils, 100% coverage

const getExperimentsInDom = (
  includeExperimentsWithPreventActivation: boolean
): ExperimentName[] => {
  // Experiments wrapped in Experiment component will be caught with this.
  // We do it this way b/c we don't want to activate all features but just the ones
  // that are rendered for that user and ones that we've manually setup to be
  // activated (ones comming from experimentsToManuallyActivate)
  return [
    ...document.querySelectorAll<HTMLElement>(
      includeExperimentsWithPreventActivation
        ? '[data-experiment]'
        : '[data-experiment]:not([data-prevent-activation])'
    )
  ].map(({ dataset: { experiment } }) => experiment as ExperimentName)
}

const getExperimentsInCookie = (
  experiments: Experiment[]
): ExperimentName[] => {
  // We only activate the features from experiments
  // If a feature has variant variable, it is from an experiment
  return experiments.filter(exp => exp?.variant).map(exp => exp?.name)
}

export const getExperimentsToActivate = ({
  includeExperimentsWithPreventActivation = false
} = {}): ExperimentName[] => {
  const {
    pageProperties: {
      experiments: experimentsRaw,
      experimentsShown: experimentsShownRaw
    }
  } = store.getPublicRuntimeConfig()
  const experiments = experimentsRaw || []
  const experimentsShown = experimentsShownRaw || []

  const experimentsInCookie: ExperimentName[] = getExperimentsInCookie(
    experiments
  )
  const experimentsInDOM: ExperimentName[] = getExperimentsInDom(
    includeExperimentsWithPreventActivation
  )

  const uniqueExperiments = new Set([
    ...MANUALLY_ACTIVATED,
    ...experimentsInDOM
  ])

  const experimentsToActivate = Array.from(uniqueExperiments)
    // only activate these which are also present in the cookie
    .filter((exp: ExperimentName) => experimentsInCookie.includes(exp))
    .filter(
      (exp: ExperimentName) =>
        !(FIRST_IMPRESSION.includes(exp) && experimentsShown.includes(exp))
    )

  return experimentsToActivate as ExperimentName[]
}

export const activateExperiment = (name: ExperimentName) => {
  const { clientFockService } = buildClientAPI()
  clientFockService.activateExperiments([name])
}

export const activateExperiments = () => {
  const experimentsToActivate = getExperimentsToActivate()

  if (experimentsToActivate?.length) {
    const { clientFockService } = buildClientAPI()
    clientFockService.activateExperiments(experimentsToActivate)
  }
}

// For use in case of hydrated react components, client
// scripts or when you know what needs to be triggered but can't implement
// using Track component for some reason (ie event that is different then click).
// eslint-disable-next-line max-params
export const trackEvent = (eventName, eventTags = {}) => {
  const { clientFockService } = buildClientAPI()

  const { cookie = '' } = document

  const econdaSessionId = getCookieByName(
    ECONDA_SESSION_ID_COOKIE,
    cookie
  )?.split(':')[0]

  const econdaVisitorId = getCookieByName(
    ECONDA_VISITOR_ID_COOKIE,
    cookie
  )?.split(':')[0]

  const eventTagsWithEcondaIds = {
    ...eventTags,
    econdaSessionId,
    econdaVisitorId
  }

  clientFockService.trackExperiment(eventName, eventTagsWithEcondaIds)
}

export const startTrackingExperiments = () => {
  document
    .querySelectorAll<HTMLElement>('[data-tracking-event]')
    .forEach((el: HTMLElement) => {
      el.addEventListener('click', function injectedTrackingEvent() {
        // TODO: see about propagation, bubbling and everything necessary so
        // that this event doesn't affect inner ones
        const { trackingEvent: event, trackingData: data } = el.dataset
        trackEvent(event, data)
      })
    })
}

export const isExperimentEnabled = (nameToFind: ExperimentName): boolean => {
  const experimentsToActivate = getExperimentsToActivate({
    includeExperimentsWithPreventActivation: true
  })

  return experimentsToActivate.some(name => name === nameToFind)
}

export const isExperimentActive = (
  name: ExperimentName,
  experiments: Experiment[]
): boolean => {
  return experiments.some(
    experiment =>
      experiment.name === name && experiment.variant !== DEFAULT_VARIANT_NAME
  )
}

export const isAnyExperimentActive = (
  names: ExperimentName[],
  experiments: Experiment[]
): boolean =>
  experiments.some(
    ({ name, variant }) =>
      variant !== DEFAULT_VARIANT_NAME && names.includes(name)
  )

export const getExperiments = (
  names: ExperimentName[],
  experiments: Experiment[]
): Experiment[] => experiments.filter(({ name }) => names.includes(name))

export const bindTrackingEvents = (defs: TrackingEvent[]) =>
  defs.forEach(({ selector, event, trackingEvent }) => {
    document.querySelectorAll<HTMLElement>(selector).forEach(element => {
      element.addEventListener(event, () => trackEvent(trackingEvent))
    })
  })
